import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PrayerIcon from '@mui/icons-material/MosqueOutlined';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SelfImprovementOutlinedIcon from '@mui/icons-material/SelfImprovementOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import MoodOutlinedIcon from '@mui/icons-material/MoodOutlined';
import axios from 'axios';
import moment from 'moment';
import config from '../config/config.json';
import { useAuth } from '../context/AuthContext';

function SummaryMetric({ icon, label, value, tone = 'primary', suffix, helper }) {
  const tones = {
    primary: {
      bg: '#EFF6FF',
      iconBg: '#DBEAFE',
      iconColor: '#2563EB',
      valueColor: '#1D4ED8',
    },
    success: {
      bg: '#ECFDF5',
      iconBg: '#D1FAE5',
      iconColor: '#059669',
      valueColor: '#047857',
    },
    warning: {
      bg: '#FFF7ED',
      iconBg: '#FFEDD5',
      iconColor: '#EA580C',
      valueColor: '#C2410C',
    },
    neutral: {
      bg: '#F8FAFC',
      iconBg: '#E2E8F0',
      iconColor: '#475569',
      valueColor: '#0F172A',
    },
  };

  const theme = tones[tone] || tones.primary;

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '16px',
        border: '1px solid #E5E7EB',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '20px',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            backgroundColor: theme.iconBg,
            color: theme.iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.5 }}>
            {label}
          </Typography>
          <Typography sx={{ fontSize: '28px', fontWeight: 800, color: theme.valueColor, lineHeight: 1.1 }}>
            {value}
            {suffix ? (
              <Typography component="span" sx={{ ml: 0.75, fontSize: '13px', fontWeight: 600, color: '#6B7280' }}>
                {suffix}
              </Typography>
            ) : null}
          </Typography>
          {helper ? (
            <Typography sx={{ fontSize: '12px', color: '#94A3B8', mt: 0.75 }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

function DetailItem({ icon, label, value, tone = 'primary' }) {
  const tones = {
    primary: { bg: '#EFF6FF', color: '#2563EB' },
    success: { bg: '#ECFDF5', color: '#059669' },
    warning: { bg: '#FFF7ED', color: '#EA580C' },
    danger: { bg: '#FEF2F2', color: '#DC2626' },
    neutral: { bg: '#F8FAFC', color: '#475569' },
  };

  const theme = tones[tone] || tones.primary;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        p: 2,
        backgroundColor: '#F8FAFC',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          backgroundColor: theme.bg,
          color: theme.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#111827', wordBreak: 'break-word' }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function PrayerDebtCard({ label, debt, prayed, tone }) {
  const tones = {
    fajr: { bg: '#FEFCE8', border: '#FDE68A', label: '#A16207', value: '#CA8A04' },
    dhuhr: { bg: '#FFF7ED', border: '#FED7AA', label: '#C2410C', value: '#EA580C' },
    asr: { bg: '#EFF6FF', border: '#BFDBFE', label: '#1D4ED8', value: '#2563EB' },
    maghrib: { bg: '#FEF2F2', border: '#FECACA', label: '#B91C1C', value: '#DC2626' },
    isha: { bg: '#F5F3FF', border: '#DDD6FE', label: '#6D28D9', value: '#7C3AED' },
  };

  const theme = tones[tone] || tones.fajr;

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: theme.bg,
        borderRadius: '12px',
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: theme.label }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 800, color: theme.value }}>
          {debt}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
        Kılınan: {prayed || 0}
      </Typography>
    </Box>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        border: '1px solid #E5E7EB',
      }}
    >
      <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: '#6B7280', fontSize: '14px', mb: 3 }}>
          {subtitle}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function formatDate(value, fallback = '-') {
  if (!value) return fallback;
  return moment(value).format('DD MMMM YYYY, HH:mm');
}

function formatRelative(value, fallback = '-') {
  if (!value) return fallback;
  return moment(value).fromNow();
}

function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const response = await axios.get(`${config.api_base}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.statusCode === 200) {
        setUserData(response.data.data);
      } else {
        setUserData(null);
        setErrorMessage('Kullanıcı detayları alınamadı.');
      }
    } catch (error) {
      setUserData(null);
      setErrorMessage(error?.response?.data?.message || 'Kullanıcı detayları alınamadı.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box>
        <SectionCard title="Kullanıcı bulunamadı">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/users')}
            sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Kullanıcılara Dön
          </Button>
          <Typography sx={{ color: '#6B7280' }}>
            {errorMessage || 'İstenen kullanıcı kaydı görüntülenemedi.'}
          </Typography>
        </SectionCard>
      </Box>
    );
  }

  const {
    user,
    stats = {},
    subscription = {},
    social = {},
    support = {},
    achievement = {},
    hatim = {},
    mood = {},
    tesbih = {},
    nafile = {},
    activity = {},
  } = userData;

  const registeredAt = user.registerDate || user.createdAt;
  const membershipDays = moment().diff(moment(registeredAt), 'days');
  const lastActivityAt =
    activity.recentActivities?.[0]?.createdAt ||
    stats.lastPrayerActionAt ||
    stats.lastFastingActionAt ||
    stats.lastPrayerDate ||
    stats.lastFastingDate;
  const prayerBreakdown = stats.prayerBreakdown || {};
  const fastingBreakdown = stats.fastingBreakdown || {};
  const kazaDebtByPrayer = stats.kazaDebtByPrayer || {};
  const kazaPrayedByPrayer = stats.kazaPrayedByPrayer || {};
  const recentTickets = support.recentTickets || [];
  const recentActivities = activity.recentActivities || [];

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E5E7EB',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={3}
          alignItems={{ xs: 'flex-start', lg: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/users')}
              sx={{
                minWidth: 140,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                alignSelf: 'flex-start',
              }}
            >
              Geri Dön
            </Button>

            <Avatar
              sx={{
                width: 64,
                height: 64,
                fontSize: '24px',
                fontWeight: 700,
                backgroundColor: '#DBEAFE',
                color: '#2563EB',
              }}
            >
              {(user.name || user.email || 'U').charAt(0).toUpperCase()}
            </Avatar>

            <Box>
              <Typography sx={{ fontSize: '30px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                {user.name || 'İsimsiz Kullanıcı'}
              </Typography>
              <Typography sx={{ color: '#6B7280' }}>
                {user.email}
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <Chip
              label={user.isActive !== false ? 'Aktif Hesap' : 'Engelli Hesap'}
              sx={{
                backgroundColor: user.isActive !== false ? '#DCFCE7' : '#FEE2E2',
                color: user.isActive !== false ? '#15803D' : '#B91C1C',
                fontWeight: 700,
                borderRadius: '10px',
                height: 36,
              }}
            />
            <Chip
              label={`${subscription.type || 'free'}${subscription.plan ? ` · ${subscription.plan}` : ''}`}
              sx={{
                backgroundColor: subscription.type === 'pro' ? '#FEF3C7' : subscription.type === 'standard' ? '#DBEAFE' : '#F8FAFC',
                color: subscription.type === 'pro' ? '#B45309' : subscription.type === 'standard' ? '#1D4ED8' : '#475569',
                fontWeight: 700,
                borderRadius: '10px',
                height: 36,
                textTransform: 'capitalize',
              }}
            />
            <Chip
              label={`${membershipDays} gün üyelik`}
              sx={{
                backgroundColor: '#F8FAFC',
                color: '#475569',
                fontWeight: 600,
                borderRadius: '10px',
                height: 36,
              }}
            />
          </Stack>
        </Stack>
      </Box>

      {errorMessage ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '20px',
          marginBottom: '24px',
        }}
      >
        <SummaryMetric
          icon={<PrayerIcon sx={{ fontSize: 26 }} />}
          label="Toplam Namaz"
          value={stats.totalPrayers}
          tone="primary"
          helper={`${stats.activePrayerDays || 0} aktif gün`}
        />
        <SummaryMetric
          icon={<FastfoodIcon sx={{ fontSize: 26 }} />}
          label="Toplam Oruç"
          value={stats.totalFastings}
          tone="success"
          helper={`${fastingBreakdown.ramadan || 0} Ramazan · ${fastingBreakdown.kaza || 0} Kaza`}
        />
        <SummaryMetric
          icon={<TrendingUpIcon sx={{ fontSize: 26 }} />}
          label="Kaza Borcu"
          value={stats.totalKazaDebt}
          suffix="rekat"
          tone="warning"
          helper={`${stats.totalKazaPrayed || 0} rekat kılınmış`}
        />
        <SummaryMetric
          icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 26 }} />}
          label="Seviye"
          value={achievement.level || 1}
          tone="neutral"
          helper={`${achievement.totalXP || 0} XP · ${achievement.unlockedCount || 0} başarı`}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' }, gap: '24px', mb: 3 }}>
        <SectionCard title="Kişisel Bilgiler" subtitle="Hesap, üyelik ve son kullanım bilgileri.">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '16px' }}>
            <DetailItem icon={<EmailIcon sx={{ fontSize: 24 }} />} label="Email" value={user.email} tone="primary" />
            <DetailItem icon={<PersonIcon sx={{ fontSize: 24 }} />} label="Kullanıcı Adı" value={user.name || user.username || '-'} tone="success" />
            <DetailItem icon={<CalendarTodayIcon sx={{ fontSize: 24 }} />} label="Kayıt Tarihi" value={formatDate(registeredAt)} tone="warning" />
            <DetailItem icon={<AccessTimeOutlinedIcon sx={{ fontSize: 24 }} />} label="Son Aktivite" value={formatRelative(lastActivityAt, 'Aktivite yok')} tone="neutral" />
            <DetailItem icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 24 }} />} label="Abonelik Durumu" value={`${subscription.type || 'free'}${subscription.status ? ` · ${subscription.status}` : ''}`} tone="primary" />
            <DetailItem icon={<CalendarTodayIcon sx={{ fontSize: 24 }} />} label="Abonelik Bitişi" value={formatDate(subscription.endDate)} tone="warning" />
          </Box>
        </SectionCard>

        <SectionCard title="Sosyal ve Destek Özeti" subtitle="Kullanıcının paneldeki etkileşim özeti.">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', mb: 3 }}>
            <Box sx={{ p: 2.5, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75 }}>Aile</Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#111827' }}>{social.family?.accepted || 0}</Typography>
              <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>{social.family?.pending || 0} bekleyen davet</Typography>
            </Box>
            <Box sx={{ p: 2.5, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75 }}>Arkadaşlık</Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#111827' }}>{social.friendships?.friends || 0}</Typography>
              <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>{social.friendships?.pending || 0} bekleyen istek</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Stack spacing={1.5}>
            <DetailItem icon={<SupportAgentOutlinedIcon sx={{ fontSize: 24 }} />} label="Açık Destek Talebi" value={support.openTickets || 0} tone="danger" />
            <DetailItem icon={<SelfImprovementOutlinedIcon sx={{ fontSize: 24 }} />} label="Aktif Hatim" value={`${hatim.activeHatimProgress || 0}% ilerleme`} tone="success" />
            <DetailItem icon={<MoodOutlinedIcon sx={{ fontSize: 24 }} />} label="Son Mood Kaydı" value={mood.lastMood ? `${mood.lastMood.mood || '-'} · ${formatDate(mood.lastMood.date)}` : 'Kayıt yok'} tone="neutral" />
            <DetailItem icon={<Groups2OutlinedIcon sx={{ fontSize: 24 }} />} label="Tesbih / Nafile" value={`${tesbih.totalSessions || 0} tesbih · ${nafile.totalEntries || 0} nafile kaydı`} tone="primary" />
          </Stack>
        </SectionCard>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: '24px', mb: 3 }}>
        <SectionCard title="Namaz ve Oruç Dağılımı" subtitle="Vakit ve oruç türüne göre kırılım.">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: '12px', mb: 3 }}>
            <PrayerDebtCard label="Sabah" debt={prayerBreakdown.fajr || 0} prayed={0} tone="fajr" />
            <PrayerDebtCard label="Öğle" debt={prayerBreakdown.dhuhr || 0} prayed={0} tone="dhuhr" />
            <PrayerDebtCard label="İkindi" debt={prayerBreakdown.asr || 0} prayed={0} tone="asr" />
            <PrayerDebtCard label="Akşam" debt={prayerBreakdown.maghrib || 0} prayed={0} tone="maghrib" />
            <PrayerDebtCard label="Yatsı" debt={prayerBreakdown.isha || 0} prayed={0} tone="isha" />
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Ramazan: ${fastingBreakdown.ramadan || 0}`} sx={{ backgroundColor: '#ECFDF5', color: '#059669', fontWeight: 600 }} />
            <Chip label={`Kaza: ${fastingBreakdown.kaza || 0}`} sx={{ backgroundColor: '#FFF7ED', color: '#C2410C', fontWeight: 600 }} />
            <Chip label={`Nafile: ${fastingBreakdown.voluntary || 0}`} sx={{ backgroundColor: '#EFF6FF', color: '#2563EB', fontWeight: 600 }} />
          </Stack>
        </SectionCard>

        <SectionCard title="Kaza Dağılımı" subtitle="Her vakit için kalan ve kılınan rekat özeti.">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(5, 1fr)' },
              gap: '16px',
            }}
          >
            <PrayerDebtCard label="Sabah" debt={kazaDebtByPrayer.fajr || 0} prayed={kazaPrayedByPrayer.fajr || 0} tone="fajr" />
            <PrayerDebtCard label="Öğle" debt={kazaDebtByPrayer.dhuhr || 0} prayed={kazaPrayedByPrayer.dhuhr || 0} tone="dhuhr" />
            <PrayerDebtCard label="İkindi" debt={kazaDebtByPrayer.asr || 0} prayed={kazaPrayedByPrayer.asr || 0} tone="asr" />
            <PrayerDebtCard label="Akşam" debt={kazaDebtByPrayer.maghrib || 0} prayed={kazaPrayedByPrayer.maghrib || 0} tone="maghrib" />
            <PrayerDebtCard label="Yatsı" debt={kazaDebtByPrayer.isha || 0} prayed={kazaPrayedByPrayer.isha || 0} tone="isha" />
          </Box>
        </SectionCard>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: '24px', mb: 3 }}>
        <SectionCard title="Support ve Aktivite" subtitle="Son destek talepleri ve sistem aktiviteleri.">
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
            Son destek talepleri
          </Typography>
          {recentTickets.length === 0 ? (
            <Typography sx={{ color: '#6B7280', mb: 3 }}>Destek talebi yok.</Typography>
          ) : (
            <List dense disablePadding sx={{ mb: 3 }}>
              {recentTickets.slice(0, 4).map((ticket) => (
                <ListItem key={ticket._id} disableGutters sx={{ py: 1, borderBottom: '1px solid #F1F5F9' }}>
                  <ListItemText
                    primary={ticket.subject || 'Destek talebi'}
                    secondary={`${ticket.status || '-'} · ${formatDate(ticket.createdAt)}`}
                    primaryTypographyProps={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}
                    secondaryTypographyProps={{ fontSize: '12px', color: '#6B7280' }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#111827', mb: 1.5 }}>
            Son aktiviteler
          </Typography>
          {recentActivities.length === 0 ? (
            <Typography sx={{ color: '#6B7280' }}>Kayıtlı aktivite yok.</Typography>
          ) : (
            <List dense disablePadding>
              {recentActivities.slice(0, 5).map((item) => (
                <ListItem key={item._id} disableGutters sx={{ py: 1, borderBottom: '1px solid #F1F5F9' }}>
                  <ListItemText
                    primary={item.action || item.type || 'Aktivite'}
                    secondary={`${formatDate(item.createdAt)}${item.details ? ` · ${item.details}` : ''}`}
                    primaryTypographyProps={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}
                    secondaryTypographyProps={{ fontSize: '12px', color: '#6B7280' }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </SectionCard>

        <SectionCard title="Ruh Hali ve İbadet Araçları" subtitle="Mood, tesbih, nafile ve hatim özeti.">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', mb: 3 }}>
            <Box sx={{ p: 2.5, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75 }}>Mood Kaydı</Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#111827' }}>{mood.totalEntries || 0}</Typography>
              <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>{mood.lastMood ? `${mood.lastMood.mood || '-'} · ${formatRelative(mood.lastMood.date)}` : 'Son mood yok'}</Typography>
            </Box>
            <Box sx={{ p: 2.5, backgroundColor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.75 }}>Tesbih</Typography>
              <Typography sx={{ fontSize: '28px', fontWeight: 800, color: '#111827' }}>{tesbih.totalCount || 0}</Typography>
              <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>{tesbih.totalSessions || 0} oturum · {tesbih.completedTargets || 0} tamamlanan</Typography>
            </Box>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
            <Chip label={`İşrak: ${nafile.ishraqCompleted || 0}`} sx={{ backgroundColor: '#EFF6FF', color: '#2563EB', fontWeight: 600 }} />
            <Chip label={`Duha: ${nafile.duhaCompleted || 0}`} sx={{ backgroundColor: '#ECFDF5', color: '#059669', fontWeight: 600 }} />
            <Chip label={`Evvabin: ${nafile.awwabinCompleted || 0}`} sx={{ backgroundColor: '#FFF7ED', color: '#C2410C', fontWeight: 600 }} />
            <Chip label={`Teheccüd: ${nafile.tahajjudCompleted || 0}`} sx={{ backgroundColor: '#F5F3FF', color: '#7C3AED', fontWeight: 600 }} />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#111827', mb: 1 }}>
            Hatim özeti
          </Typography>
          <Typography sx={{ color: '#6B7280', lineHeight: 1.7 }}>
            Aktif hatim ilerlemesi: <strong>{hatim.activeHatimProgress || 0}%</strong> · Tamamlanan hatim: <strong>{hatim.completedHatims || 0}</strong>
          </Typography>
        </SectionCard>
      </Box>
    </Box>
  );
}

export default UserDetailPage;
